import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * Language object type
 */
export interface Language {
  code: string;
  name: string;
  nativeName: string;
}

/**
 * Supported languages in the portal
 */
export const SUPPORTED_LANGUAGES: readonly Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

const LANGUAGE_STORAGE_KEY = 'portal_language_preference';
const DEFAULT_LANGUAGE: LanguageCode = 'en';

interface LanguageContextType {
  languageCode: LanguageCode;
  setLanguageCode: (code: LanguageCode) => void;
  getLanguageName: (code: LanguageCode) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Language Provider Component
 * Manages global language preference state and persistence
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [languageCode, setLanguageCodeState] = useState<LanguageCode>(() => {
    // Initialize from localStorage or default
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (saved && SUPPORTED_LANGUAGES.some(lang => lang.code === saved)) {
        return saved as LanguageCode;
      }
    }
    return DEFAULT_LANGUAGE;
  });

  // Persist to localStorage when language changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
    }
  }, [languageCode]);

  const setLanguageCode = (code: LanguageCode) => {
    setLanguageCodeState(code);
  };

  const getLanguageName = (code: LanguageCode): string => {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
    return lang?.nativeName || lang?.name || code.toUpperCase();
  };

  return (
    <LanguageContext.Provider
      value={{
        languageCode,
        setLanguageCode,
        getLanguageName,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to use language context
 * Must be used within LanguageProvider
 */
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

